import { store } from 'store';
import { catchAllStartDate, getDayString } from 'tsx/libs/dayjs';

import { getAll as getAppointmentServiceTypes } from '~appointments/actions/serviceTypes';
import { getAll as getNDISSupportItems } from '~lookups/actions/ndisSupportItems';
import { ServiceTypeRow } from '~appointments/reducers/serviceTypes';
import { getClientPackageSchedules } from '~main/actions/clients/clientPackageSchedules';

import {
  derivePaymentAmount,
  deriveServiceTypeSelectorParams,
  deriveServiceTypeSupportCoordinationParams,
} from './derive';

export const onChangeServiceType: (values: any) => any = (values) => {
  const { client_id, service_type_id } = values;

  // On service type change, force qualifications to equal default service qualifications.
  const state = store.getState();
  const { serviceTypes } = state.appointments;
  const serviceType = serviceTypes.rows.find(({ id }) => parseInt(service_type_id) === id);

  let qualification_level: number[] = [];
  if (serviceType) {
    qualification_level = serviceType?.default_qualification_ids ?? [];

    // If NDIS Support Item, query for NDIS Support Items from API
    if (serviceType.ndis_support_item > 0 && client_id) {
      store.dispatch(
        getNDISSupportItems({
          client_id,
          service_type_id,
          support_item_id: serviceType.ndis_support_item,
        }),
      );
    }
  }
  return {
    qualification_level,
    payment_amount: derivePaymentAmount(values),
  };
};
export const onChangeClient: (values: any) => any = async ({ client_id, date, service_type_id }) => {
  const state = store.getState();
  const { clients } = state.main;
  const client = clients.rows.find(({ id }) => parseInt(client_id) === id);
  // If client exists, collect and match current package schedules to existing default package
  if (client) {
    // Must wait for response in this case otherwise data is not up to date
    const { payload } = await store.dispatch(getClientPackageSchedules({ client_id, date }));
    const defaultPackage = payload?.data.find(
      ({ package: pkg }: any) => pkg.id === client?.default_package?.id,
    )?.package;

    // If service_type_id exists, query for NDIS Support Items from API
    if (service_type_id) {
      const state = store.getState();
      const { serviceTypes } = state.appointments;
      const serviceType = serviceTypes.rows.find(({ id }) => parseInt(service_type_id) === id);

      if ((serviceType?.ndis_support_item ?? 0) > 0) {
        await store.dispatch(
          getNDISSupportItems({
            client_id,
            service_type_id,
            support_item_id: serviceType?.ndis_support_item,
          }),
        );
      }
    }

    return { package_fixed_id: defaultPackage?.id.toString() ?? '' };
  }
};

export const onChangePackageHandler: (
  isSupportCoordination?: boolean,
) => (values: { package_fixed_id: string }) => void =
  (isSupportCoordination = false) =>
  ({ package_fixed_id }) => {
    let supportCoordinationParams = undefined;
    if (package_fixed_id) {
      if (isSupportCoordination) {
        supportCoordinationParams = deriveServiceTypeSupportCoordinationParams(package_fixed_id);
      }
      store.dispatch(
        getAppointmentServiceTypes({
          package_id: package_fixed_id,
          ...supportCoordinationParams,
        }),
      );
    }
  };

interface ServiceTypeReloadValues {
  package_fixed_id?: string | number | null;
  service_type_id?: string | number | null;
  transport?: boolean;
  case_management?: boolean;
  [key: string]: unknown;
}

// Every package change takes a ticket; only the latest may clear the service type, so a late response for an
// earlier package cannot act on the newer one. One counter is enough: one appointment form is mounted at a time
// and they all share the one service types list (TAV-3465).
let serviceTypeReloadTicket = 0;

/**
 * Package onChange for any form whose Service Type select uses deriveServiceTypeSelectorParams: reloads the
 * list with those same params, so the package's service restrictions apply, and clears a chosen service type
 * the new package does not allow. A plain handler, not a factory, so it cannot be wired up uncalled.
 */
export const onChangePackageReloadServiceTypes = async (
  values: ServiceTypeReloadValues,
): Promise<{ service_type_id: null } | undefined> => {
  serviceTypeReloadTicket += 1;
  const ticket = serviceTypeReloadTicket;

  const { payload } = await store.dispatch(getAppointmentServiceTypes(deriveServiceTypeSelectorParams(values)));
  if (ticket !== serviceTypeReloadTicket) return undefined;

  const rows: ServiceTypeRow[] | undefined = payload?.data;
  const { service_type_id } = values;
  if (!rows || !service_type_id) return undefined;

  return rows.some(({ id }) => id === Number(service_type_id)) ? undefined : { service_type_id: null };
};

/** Client onChange: onChangeClient sets the package by return value, which skips the package onChange. */
export const onChangeClientReloadServiceTypes = async (values: ServiceTypeReloadValues) => {
  const changes = await onChangeClient(values);
  if (!changes) return changes;
  const cleared = await onChangePackageReloadServiceTypes({ ...values, ...changes });
  return { ...changes, ...cleared };
};

export const onChangeUserOrDate: (values: any) => any = ({ user_id, repeat_start }) => {
  if (user_id && repeat_start) {
    return { date: catchAllStartDate, repeat: true, repeat_weekday: getDayString(repeat_start) };
  }
};

export const onChangeTime: (values: any) => any = (values) => ({ payment_amount: derivePaymentAmount(values) });
